import * as XLSX from "xlsx";
import { eventColumnLabel, eventDateRange } from "./eventComparison";
import { incidentLink } from "./incidentLink";
import { plainText } from "./richText";
import { drugLine } from "./incidentNotes";
import { syncSheetRow } from "./personnelSync";
import {
  consentText, feedbackEventText, feedbackLocationText, resolutionsText, responseState,
} from "./feedbackRules";
import { affectedText, notificationsText, reportTypeText } from "./clinicalReportRules";
import { DRUGS_CATEGORY, DRUG_AUTHORITY_GROUPS } from "./constants";

// Shared XLSX export helpers for the Personnel and Event views.

// Local YYYY-MM-DD (matches the rest of the app; avoids UTC off-by-one).
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// A person's qualifications as text, expanding "Other" to its free-text value.
const qualificationsText = (p) => {
  const qs = p.qualifications || [];
  return qs
    .map(q => (q === "Other" && p.qualifications_other ? `Other: ${p.qualifications_other}` : q))
    .join(", ");
};

// The fields a qualification reveals are only exported for someone holding it,
// as the record form only shows them then: a grade left on a record that no
// longer says Doctor isn't the person's grade.
const holds = (p, quals) => (p.qualifications || []).some(q => quals.includes(q));
const doctorGradeText = (p) =>
  holds(p, ["Doctor", "Doctor (training)"]) ? (p.doctor_grade || "") : "";
// Blank (not "No") for anyone who isn't a nurse or paramedic, for whom the
// question doesn't arise; also blank when the record didn't come with the flag.
const prescriberText = (p) =>
  holds(p, ["Nurse", "Paramedic"]) && typeof p.independent_prescriber === "boolean"
    ? (p.independent_prescriber ? "Yes" : "No") : "";

// The personnel columns shared by both exports, in display order.
const personnelRow = (p) => ({
  Forename: p.forename || "",
  Surname: p.surname || "",
  // Not plain "Status": the event export adds the attendance's own Status column.
  "Personnel status": p.status || "",
  Email: p.email || "",
  Phone: p.phone || "",
  "Date of birth": p.date_of_birth || "",
  "Shelter Stone role": (p.roles || []).join(", "),
  Qualifications: qualificationsText(p),
  "Doctor grade": doctorGradeText(p),
  "Independent prescriber": prescriberText(p),
  "Emergency contact name": p.emergency_contact_name || "",
  "Emergency contact phone": p.emergency_contact_phone || "",
  "Clothing size": p.clothing_size || "",
  Vehicle: p.vehicle || "",
  "Vehicle registration": p.vehicle_registration || "",
  "Address line 1": p.address_line1 || "",
  "Address line 2": p.address_line2 || "",
  Town: p.town || "",
  County: p.county || "",
  Postcode: p.postcode || "",
});

const PERSONNEL_HEADERS = Object.keys(personnelRow({}));

// The event columns. The fee is pay information the API only returns to the person
// themselves and to attendance administrators (_may_see_fees in
// routers/attendance.py), so its column is left out altogether for anyone else
// rather than exported blank — which would read as "no fee agreed".
const eventHeaders = (includeFee) => [
  ...PERSONNEL_HEADERS, "Event role", "Team", "Status",
  ...(includeFee ? ["Fee"] : []), "Shift details",
];

// Write the fee as a number so it can be summed in the sheet, keeping whatever
// came back if it isn't one.
const feeCell = (fee) => {
  if (fee == null || fee === "") return "";
  const n = Number(fee);
  return Number.isFinite(n) ? n : String(fee);
};

// Replace characters that aren't safe in a filename.
const safeName = (s) => (s || "").replace(/[^\w.-]+/g, "_").replace(/^_+|_+$/g, "") || "export";

// Write an array of row objects to an .xlsx file and trigger a download.
// `headers` forces column order and keeps them present even when `rows` is empty.
//
// `linkColumn` names a column whose values are URLs and makes them clickable in
// the sheet — json_to_sheet writes them as plain strings, which Excel only turns
// into links if the cell is edited. A column of addresses nobody can follow would
// be a poor answer to "give me a link to the incident".
const writeSheet = (rows, headers, filename, sheetName, { linkColumn } = {}) => {
  const ws = rows.length
    ? XLSX.utils.json_to_sheet(rows, { header: headers })
    : XLSX.utils.aoa_to_sheet([headers]);
  const linkCol = linkColumn ? headers.indexOf(linkColumn) : -1;
  if (linkCol >= 0) {
    rows.forEach((row, i) => {
      // +1 for the header row; a row with no URL is left as it is.
      const cell = ws[XLSX.utils.encode_cell({ r: i + 1, c: linkCol })];
      if (cell && row[linkColumn]) cell.l = { Target: row[linkColumn] };
    });
  }
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);
};

// Export a list of personnel records (e.g. the currently filtered Personnel table).
export const exportPersonnel = (people) => {
  const rows = people.map(personnelRow);
  writeSheet(rows, PERSONNEL_HEADERS, `personnel-${todayISO()}.xlsx`, "Personnel");
};

// Export the people associated with an event. `attendances` carry the event-specific
// role/status/team plus their personal arrangements (fee, shift details);
// `personById` maps a personnel_id to the full personnel record. Pass
// `includeFee` when the caller may see everyone's fee — see eventHeaders.
export const exportEventPersonnel = (event, attendances, personById, { includeFee = false } = {}) => {
  const rows = attendances.map(a => {
    const p = personById[a.personnel_id] || a.person || {};
    return {
      ...personnelRow(p),
      "Event role": a.role || "",
      Team: a.team?.name || "",
      Status: a.status || "",
      ...(includeFee ? { Fee: feeCell(a.fee) } : {}),
      "Shift details": a.shift_details || "",
    };
  });
  const label = event?.label_short || event?.abbreviation || event?.name || "event";
  writeSheet(rows, eventHeaders(includeFee),
             `${safeName(label)}-personnel-${todayISO()}.xlsx`, "Personnel");
};

// One event participant as a flat row. The columns match the names the CSV import
// recognises, so an exported sheet can be saved as CSV and re-imported (e.g. to
// carry a screened entry list from one system to another).
const participantRow = (p) => ({
  Name: p.name || "",
  Race: p.race || "",
  "Race number": p.race_number || "",
  Age: p.age ?? "",
  "Date of birth": p.date_of_birth || "",
  Sex: p.sex || "",
  Phone: p.phone || "",
  Email: p.email || "",
  "Medical information": p.medical_information || "",
  "Screening status": p.screening_status || "",
  Notes: p.notes || "",
  "Watch list notes": p.watch_list_notes || "",
  "Emergency contact name": p.emergency_contact_name || "",
  "Emergency contact phone": p.emergency_contact_phone || "",
});

const PARTICIPANT_HEADERS = Object.keys(participantRow({}));

// Export an event's entry list (e.g. the currently filtered Participants table).
export const exportEventParticipants = (event, participants) => {
  const rows = participants.map(participantRow);
  const label = event?.label_short || event?.abbreviation || event?.name || "event";
  writeSheet(rows, PARTICIPANT_HEADERS, `${safeName(label)}-participants-${todayISO()}.xlsx`, "Participants");
};

// Combine the stored numeric lat/long back into the "lat, long" form (matches the UI field).
const latLong = (i) =>
  (i.location_latitude != null && i.location_longitude != null)
    ? `${i.location_latitude}, ${i.location_longitude}`
    : "";

// A datetime as a human-readable local string (blank when unset).
const dateTimeText = (iso) => (iso ? new Date(iso).toLocaleString() : "");

// One incident as a flat row, in display order.
const incidentRow = (i) => ({
  // The record's own id, first: it's what an incident is referred to by outside the
  // app — in a follow-up, or against a spreadsheet somebody has annotated — and
  // it's the only column here that identifies the row uniquely.
  ID: i.id,
  // Beside it, the address that opens the record — so a sheet being worked
  // through (a follow-up list, a set of incidents to review) leads back into the
  // app a click at a time instead of by searching for each id in turn.
  Link: i.id != null ? incidentLink(i.id) : "",
  Created: dateTimeText(i.created_at),
  Event: i.event?.label_long || "",
  "Created by": i.created_by_name || "",
  "Created by event role": i.created_by_role || "",
  "Incident type": i.incident_type || "",
  Summary: i.incident_summary || "",
  Status: i.status || "",
  "Allocated to": i.allocated_to_name || "",
  "Allocated to event role": i.allocated_to_role || "",
  "Allocation updated": dateTimeText(i.allocated_to_updated_at),
  "Patient name": i.patient_name || "",
  "Race number": i.patient_race_number || "",
  Age: i.patient_age ?? "",
  Sex: i.patient_sex || "",
  Race: i.patient_race || "",
  "Patient phone": i.patient_phone || "",
  // Written even when blank — "we don't have an address for this one" is part of
  // the answer when the sheet is a follow-up list.
  "Patient email": i.patient_email || "",
  "Location type": i.location_type || "",
  "Location details": i.location_details || "",
  what3words: i.location_what3words || "",
  "Grid reference": i.location_grid_ref || "",
  Postcode: i.location_postcode || "",
  "Lat/long": latLong(i),
  "Detail type": i.detail_type || "",
  "Sub-type": i.detail_subtype || "",
  // Its own column so Sub-type stays one of the list's values, to filter or pivot on.
  "Sub-type (Other)": i.detail_other || "",
  // Rich text (Markdown) in the app; a cell can hold neither the formatting nor
  // the embedded photos, so both are flattened — a photo leaves a "[photo]".
  "Incident details": plainText(i.incident_details),
  "Response Team deployment": i.response_team_deployment || "",
  "Mountain Safety deployment": i.mountain_safety_deployment || "",
  "Disposal transport": i.disposal_transport || "",
  "Disposal destination": i.disposal_destination || "",
  "With whom": i.with_whom || "",
  "Phone status": i.phone_status || "",
  "Alternative phones": i.alternative_phones || "",
  Experience: i.experience || "",
  Weather: i.weather || "",
  Welfare: i.welfare || "",
  "Patient consent": i.patient_consent || "",
  "Consent details": i.patient_consent_details || "",
  "Child consent given by": i.patient_consent_child_by || "",
  "Treatment given": i.treatment_given || "",
  // The advice categories by name, comma-separated; blank = none recorded.
  "Safety netting": (i.safety_netting || []).join(", "),
  "Advised to retire": i.advised_to_retire || "",
  "Consents to teaching images": i.consent_teaching_images ? "Yes" : "",
  "Requires follow up": i.consent_followup ? "Yes" : "",
  "Clinical reviewed by": i.clinical_review_by || "",
  "Clinical reviewed at": dateTimeText(i.clinical_review_at),
  "Drugs given": drugsText(i.drug_administrations),
  Notes: notesText(i.notes),
});

// The drugs given on an incident as one cell, oldest first — voided ones left out,
// since they were recorded in error.
const drugsText = (drugs) => (drugs || [])
  .filter(d => !d.voided_at)
  .slice()
  .sort((a, b) => (a.given_at || "").localeCompare(b.given_at || ""))
  .map(d => [drugLine(d), d.batch_number ? `batch ${d.batch_number}` : null,
             d.notes, `(${dateTimeText(d.given_at)}${d.given_by ? ` — ${d.given_by}` : ""})`]
    .filter(Boolean).join(", "))
  .join("\n");

// All notes on an incident as one cell: each note's text with its attribution,
// oldest first, separated by blank lines.
const notesText = (notes) => (notes || [])
  .slice()
  .sort((a, b) => (a.added_at || "").localeCompare(b.added_at || ""))
  .map(n => {
    const who = [n.added_by, n.added_by_role].filter(Boolean).join(", ");
    const meta = [dateTimeText(n.added_at), who].filter(Boolean).join(" — ");
    return `${plainText(n.details)}${meta ? `\n(${meta})` : ""}`;
  })
  .join("\n\n");

const INCIDENT_HEADERS = Object.keys(incidentRow({}));

// Export a list of incidents (e.g. the currently filtered Incidents table). The list
// is normally for one event, so the file is named after it — falling back to the event
// on the first row when the caller doesn't pass one.
//
// `allEvents` is for the one export that spans them (the Super Admin's whole-database
// sheet) and says so in the filename. It has to be told: an absent `event` can't mean
// "no event" on its own, since that's also how a single-event list arrives when the
// caller doesn't have the event to hand — and there the first row's event is the right
// answer, while here it would name the file after an arbitrary one of the many in it.
export const exportIncidents = (incidents, event, { allEvents = false } = {}) => {
  const rows = incidents.map(incidentRow);
  const e = allEvents ? null : (event || incidents[0]?.event);
  const label = e?.label_short || e?.abbreviation || e?.name;
  const stem = [label ? safeName(label) : null, "incidents",
                allEvents ? "all-events" : null, todayISO()].filter(Boolean).join("-");
  writeSheet(rows, INCIDENT_HEADERS, `${stem}.xlsx`, "Incidents", { linkColumn: "Link" });
};

// Inventory items export two ways: a row per item (below), or a row per batch
// (further down). Both take the same `resolvers` — the page's lookups for the
// joins the table displays.

// One inventory item as a flat row, in display order. For a batched (Drugs) item
// Quantity is the total across its batches and the batches themselves are left out
// — use the per-batch export for those. `typeName`/`category`/`location` resolve the
// joins the table shows (type name, its category, the container path), and `name` is
// the page's own itemLabel: an item has no name of its own, so the sheet leads with
// the same combination of details, type, serial number and size that the table
// displays, and keeps the parts it's built from in the columns after it.
const inventoryItemRow = (i, { typeName = () => "", category = () => "", location = () => "", name = () => "" } = {}) => ({
  "#": i.id ?? "",
  Name: name(i),
  Category: category(i.type_id),
  Type: typeName(i.type_id),
  Details: i.details || "",
  Size: i.size || "",
  Location: location(i),
  "Required quantity": i.required_quantity ?? "",
  Quantity: i.quantity ?? "",
  "Serial number": i.serial_number || "",
  "Expiry date": i.expiry_date || "",
  "Last checked date": i.last_checked_date || "",
  "Last checked by": i.last_checked_by || "",
  Notes: i.notes || "",
});

const INVENTORY_ITEM_HEADERS = Object.keys(inventoryItemRow({}));

// Export a list of inventory items (e.g. the currently filtered Items table).
// `resolvers` carries the page's typeLabel/location lookups.
export const exportInventoryItems = (items, resolvers) => {
  const rows = items.map(i => inventoryItemRow(i, resolvers));
  writeSheet(rows, INVENTORY_ITEM_HEADERS, `inventory-items-${todayISO()}.xlsx`, "Inventory items");
};

// One batch as a flat row: same column order as the per-item sheet, with the item's
// shared details (category, type, location, name, what it should be stocked at)
// repeated and that pack's own figures alongside. Both ids lead, so a row identifies
// the item it belongs to and the batch itself; "Batch #" is blank for an unbatched
// item. Name is the item's, so it carries no serial number for a batched one — that
// belongs to the pack, and has a column of its own below.
const inventoryBatchRow = (i, b, { typeName = () => "", category = () => "", location = () => "", name = () => "" } = {}) => ({
  "#": i.id ?? "",
  "Batch #": b.id ?? "",
  Name: name(i),
  Category: category(i.type_id),
  Type: typeName(i.type_id),
  Details: i.details || "",
  Size: i.size || "",
  Location: location(i),
  "Required quantity": i.required_quantity ?? "",
  "Total quantity": i.quantity ?? "",
  "Batch quantity": b.quantity ?? "",
  "Opened": b.opened || "",
  // A batched item's batch number, or an unbatched item's own serial number.
  "Batch / serial number": b.serial_number || "",
  "Expiry date": b.expiry_date || "",
  "Last checked date": i.last_checked_date || "",
  "Last checked by": i.last_checked_by || "",
  Notes: i.notes || "",
});

const INVENTORY_BATCH_HEADERS = Object.keys(inventoryBatchRow({}, {}));

// Export inventory items one row per batch, so a drug item held as three packs is
// three rows differing only in quantity/serial/expiry. An item with no batches still
// gets one row, filled from its own quantity/serial/expiry, so the sheet is a
// complete stock list rather than drugs only.
export const exportInventoryBatches = (items, resolvers) => {
  const rows = items.flatMap(i => {
    const batches = i.batches?.length
      ? i.batches
      : [{ quantity: i.quantity, serial_number: i.serial_number, expiry_date: i.expiry_date }];
    return batches.map(b => inventoryBatchRow(i, b, resolvers));
  });
  writeSheet(rows, INVENTORY_BATCH_HEADERS, `inventory-batches-${todayISO()}.xlsx`, "Inventory batches");
};

// One inventory type as a flat row, in display order. The expiry grace is left blank
// rather than written as 0 when there is none, matching the page: no grace means the
// printed expiry date is the deadline.
const inventoryTypeRow = (t) => ({
  "#": t.id ?? "",
  Name: t.name || "",
  Category: t.category || "",
  Notes: t.notes || "",
  "Expiry grace (years)": t.expiry_grace_years ?? "",
  Allocatable: t.can_allocate ? "Yes" : "",
  // The Drugs-only formulary fields, blank on every other category.
  "Common name": t.drug_common_name || "",
  Presentation: t.drug_presentation || "",
  Route: (t.drug_routes || []).join(" or "),
  "Single dose": t.drug_dose_quantity != null
    ? `${t.drug_dose_quantity} ${t.drug_dose_unit || ""}`.trim() : t.drug_dose_unit || "",
  Class: t.drug_class || "",
  "Legal category": t.drug_legal_category || "",
  "Schedule 17": t.category === DRUGS_CATEGORY ? (t.drug_schedule_17 ? "Yes" : "No") : "",
  "Schedule 19": t.category === DRUGS_CATEGORY ? (t.drug_schedule_19 ? "Yes" : "No") : "",
  ...Object.fromEntries(DRUG_AUTHORITY_GROUPS.map(g =>
    [`Authority: ${g.label}`, t[`drug_authority_${g.key}`] || ""])),
  "Reorder supplier": t.reorder_supplier || "",
  "Reorder link": t.reorder_link || "",
  "Reorder price": t.reorder_price ?? "",
});

const INVENTORY_TYPE_HEADERS = Object.keys(inventoryTypeRow({}));

// Export a list of inventory types (e.g. the currently filtered Types table).
export const exportInventoryTypes = (types) => {
  const rows = types.map(inventoryTypeRow);
  writeSheet(rows, INVENTORY_TYPE_HEADERS, `inventory-types-${todayISO()}.xlsx`, "Inventory types");
};

// One inventory allocation as a flat row: what went out, to whom, and where it
// stands. The item's parts (serial number, category, type, container path) each keep
// a column of their own after the composed name, as they do on the items sheet, so
// the sheet can be sorted and filtered on any of them — the on-screen ordering is
// only the one the reader happened to ask for.
const allocationRow = (a) => ({
  Item: a.item?.name || "",
  "Serial number": a.item?.serial_number || "",
  Category: a.item?.category || "",
  Type: a.item?.type_name || "",
  Location: a.item?.location || "",
  Person: a.person?.name || "",
  // Their attendance role on the event this allocation is for.
  "Event role": a.person?.role || "",
  Event: a.event?.label_long || "",
  Status: a.status || "",
  Notes: a.notes || "",
});

const ALLOCATION_HEADERS = Object.keys(allocationRow({}));

// Export a list of allocations — the rows exactly as ordered and filtered on screen,
// since that ordering is what the reader asked for. `title` names what they are for
// (an event's label) and goes in the filename.
export const exportAllocations = (allocations, { title } = {}) => {
  const rows = allocations.map(allocationRow);
  // safeName() falls back to "export" for a blank, which would read as a filename
  // part rather than an absent one — so only ask it about a title we actually have.
  const stem = ["allocations", title ? safeName(title) : null, todayISO()]
    .filter(Boolean).join("-");
  writeSheet(rows, ALLOCATION_HEADERS, `${stem}.xlsx`, "Allocations");
};

// Two events can share a label (same name, same year), and a sheet row is an
// object keyed by header — so a repeat would silently overwrite the earlier
// column rather than stand beside it. Number the repeats instead.
const uniqueHeaders = (labels) => {
  const seen = new Map();
  return labels.map(label => {
    const n = (seen.get(label) || 0) + 1;
    seen.set(label, n);
    return n === 1 ? label : `${label} (${n})`;
  });
};

// The multi-event comparison: a row per count, a column per event. `sections` is
// the report exactly as the dialog shows it (buildSections in eventComparison.js),
// so the sheet can't say something different from the screen — including which
// breakdowns were left out because the events don't share a name.
//
// A section's title is a row of its own with no figures, which is how a flat sheet
// carries the grouping; a sub-type is indented under its detail type the same way.
export const exportEventComparison = (sections, events) => {
  const columns = uniqueHeaders(events.map(eventColumnLabel));
  const headers = ["Metric", ...columns];
  const row = (label, values = []) => {
    const out = { Metric: label };
    columns.forEach((h, i) => { out[h] = values[i] ?? ""; });
    return out;
  };
  const rows = [
    row("Dates", events.map(eventDateRange)),
    ...sections.flatMap((s, i) => [
      // A blank row between breakdowns, as on screen — one before each section
      // but the first, which already follows the dates. An all-empty row object
      // writes a genuinely empty sheet row.
      ...(i > 0 ? [row("")] : []),
      row(s.title),
      ...s.rows.map(r => row(r.level ? `    ${r.label}` : r.label, r.values)),
    ]),
  ];
  writeSheet(rows, headers, `event-comparison-${todayISO()}.xlsx`, "Comparison");
};

// One audit-log entry as a flat row, matching the Audit table's columns/order.
// `eventLabel`/`metaSummary` are passed in from the page so the sheet stays in
// step with what's displayed (event names and the human meta summary).
const auditRow = (r, { eventLabel = () => "", metaSummary = () => "" } = {}) => ({
  When: dateTimeText(r.at),
  Actor: r.actor_name || r.actor_email || "",
  Action: r.action || "",
  Table: r.target_type || "",
  Name: r.target_name || "",
  Target: r.target_id ?? "",
  Event: r.event_id != null ? eventLabel(r.event_id) : "",
  Detail: metaSummary(r.meta) || "",
});

const AUDIT_HEADERS = Object.keys(auditRow({}));

// Export the currently visible audit-log entries.
export const exportAuditLog = (entries, resolvers) => {
  const rows = entries.map(r => auditRow(r, resolvers));
  writeSheet(rows, AUDIT_HEADERS, `audit-log-${todayISO()}.xlsx`, "Audit log");
};

// One stock ledger line as a flat row, matching the Stock history table. `kindLabel`
// is passed in so the sheet uses the page's words for each kind.
const stockHistoryRow = (r, { kindLabel = (k) => k } = {}) => ({
  When: dateTimeText(r.at),
  What: kindLabel(r.kind),
  Item: r.type_name || "",
  Category: r.category || "",
  Batch: r.batch_number || "",
  "Batch expiry": r.expiry_date || "",
  Where: r.item_label || "",
  Change: r.change ?? "",
  "Held there after": r.balance_after ?? "",
  Expected: r.expected ?? "",
  Counted: r.counted ?? "",
  Reason: r.reason || "",
  By: r.actor_name || r.actor_email || "",
  Reference: r.reference || "",
  "Handled by": r.handled_by || "",
  Incident: r.incident_ref ?? "",
  Person: r.personnel_name || "",
  Event: r.event_label || "",
  "Needs investigation": r.needs_investigation ? "Yes" : "",
  Notes: r.notes || "",
  "Transaction #": r.transaction_id ?? "",
});

const STOCK_HISTORY_HEADERS = Object.keys(stockHistoryRow({}));

export const exportStockHistory = (rows, resolvers) => {
  writeSheet(rows.map(r => stockHistoryRow(r, resolvers)), STOCK_HISTORY_HEADERS,
             `stock-history-${todayISO()}.xlsx`, "Stock history");
};

// The stock report, one row per batch held at each place — the shape every question
// of it is answered from in a spreadsheet (filter by expiry, pivot by item). `flag`
// is the page's verdict on the expiry, so the sheet says what the screen says.
const stockReportRow = (t, line, h, { flag = () => "" } = {}) => ({
  Item: t.name || "",
  Category: t.category || "",
  Line: line.label || "",
  Where: line.where || "",
  Role: line.stock_role || "",
  "Required here": line.required_quantity ?? "",
  "Held here": line.quantity ?? "",
  Batch: h.batch_number || "",
  "Batch expiry": h.expiry_date || "",
  "Batch quantity": h.quantity ?? "",
  Opened: h.opened || "",
  Recalled: h.recalled ? "Yes" : "",
  "Expiry flag": flag(t, h) || "",
  "Total held": t.held ?? "",
  "Total required": t.required ?? "",
  "In stock cupboards": t.in_store ?? "",
});

const STOCK_REPORT_HEADERS = Object.keys(stockReportRow({}, {}, {}));

export const exportStockReport = (types, resolvers) => {
  const rows = types.flatMap(t => t.lines.flatMap(line => (line.holdings.length
    ? line.holdings.map(h => stockReportRow(t, line, h, resolvers))
    : [stockReportRow(t, line, {}, resolvers)])));
  writeSheet(rows, STOCK_REPORT_HEADERS, `stock-report-${todayISO()}.xlsx`, "Stock report");
};

// The personnel sync check's discrepancies, as filtered on screen
// (pages/PersonnelSync.jsx). Columns come from personnelSync.syncSheetRow, so the
// sheet and the page use the same words.
export const exportPersonnelSync = (rows) => {
  const out = rows.map(syncSheetRow);
  writeSheet(out, Object.keys(syncSheetRow({ issues: [] })),
             `personnel-sync-${todayISO()}.xlsx`, "Discrepancies");
};

// Export the Feedback page's list exactly as filtered on screen.
const FEEDBACK_HEADERS = [
  "Reference", "Submitted", "Source", "Type", "Status", "Response due", "Event", "Location",
  "From", "Phone", "Email", "On behalf of", "Consent", "Details",
  "Linked entrant", "Resolutions", "Notes", "IP address",
];

const feedbackRow = (fb) => ({
  Reference: fb.reference,
  Submitted: fb.submitted_at ? new Date(fb.submitted_at).toLocaleString() : "",
  Source: fb.source === "staff" ? "Staff" : "Public form",
  Type: (fb.nature || []).join(", "),
  Status: fb.status || "",
  "Response due": (() => {
    const r = responseState(fb);
    if (!r) return "";
    return `${r.due.toLocaleDateString()}${r.state === "overdue" ? " (overdue)" : ""}`;
  })(),
  Event: feedbackEventText(fb),
  Location: feedbackLocationText(fb),
  From: fb.submitter_name || "",
  Phone: fb.submitter_phone || "",
  Email: fb.submitter_email || "",
  "On behalf of": fb.on_behalf_of || "",
  Consent: consentText(fb),
  Details: fb.details || "",
  "Linked entrant": fb.participant_label || "",
  Resolutions: resolutionsText(fb),
  // Oldest first, one per line, each signed and dated as on screen.
  Notes: (fb.notes || [])
    .map(n => `${n.added_at ? new Date(n.added_at).toLocaleString() : ""} ${n.added_by || ""}: ${n.details || ""}`)
    .join("\n"),
  "IP address": fb.ip || "",
});

export const exportFeedback = (rows) => {
  writeSheet(rows.map(feedbackRow), FEEDBACK_HEADERS, `feedback-${todayISO()}.xlsx`, "Feedback");
};

// Export the Clinical reports page's list exactly as filtered on screen.
const CLINICAL_REPORT_HEADERS = [
  "Reference", "Happened", "Raised", "Raised by", "Type", "Event", "Status", "Degree of harm",
  "Person affected", "Linked entrant", "Phone", "Email", "Related incident", "Details",
  "Duty of candour", "Notifications", "Notes",
];

const dt = (iso) => (iso ? new Date(iso).toLocaleString() : "");

const clinicalReportRow = (r) => ({
  Reference: r.reference,
  Happened: dt(r.occurred_at),
  Raised: dt(r.created_at),
  "Raised by": r.created_by || "",
  Type: reportTypeText(r),
  Event: r.event_label || "",
  Status: r.status || "",
  "Degree of harm": r.harm || "Not assessed",
  "Person affected": affectedText(r),
  "Linked entrant": r.participant_label || "",
  Phone: r.affected_phone || "",
  Email: r.affected_email || "",
  "Related incident": r.incident_id != null ? `#${r.incident_id}` : "",
  Details: r.details || "",
  "Duty of candour": r.candour_at ? `${dt(r.candour_at)}${r.candour_by ? ` by ${r.candour_by}` : ""}` : "",
  Notifications: notificationsText(r),
  // Oldest first, one per line, each signed and dated as on screen.
  Notes: (r.notes || [])
    .map(n => `${dt(n.added_at)} ${n.added_by || ""}: ${n.details || ""}`)
    .join("\n"),
});

export const exportClinicalReports = (rows) => {
  writeSheet(rows.map(clinicalReportRow), CLINICAL_REPORT_HEADERS,
             `clinical-reports-${todayISO()}.xlsx`, "Clinical reports");
};
